import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useUserManagementAccess } from './useUserManagementAccess';

/**
 * Route-level gate for the User Management pages (scio3#2351).
 *
 * `useUserManagementAccess` governs NAVIGATION — the Settings side panel and the top-bar shortcuts —
 * so a role that may not use a page simply has no link to it. The ROUTES had no gate at all, so the
 * same page still rendered in full for anyone who typed or bookmarked its URL. The API now withholds
 * the sensitive data (open-api!1659), so this is no longer a disclosure, but a non-admin landing on a
 * page of withheld panels reads as a broken app rather than as "not for you".
 *
 * Deliberately a ROUTE wrapper rather than a check inside the page components: `MembersPage` is also
 * rendered as a POP-UP by the Teamspaces page, and a `<Navigate>` fired from inside it would redirect
 * the whole app out from under that dialog.
 *
 * Redirects to the same place `AuditLogPage` does, so an unauthorized deep link behaves consistently
 * across settings.
 */
export function UserManagementRouteGuard({
  area,
  children,
}: {
  /** Which entry of `useUserManagementAccess` governs this route. */
  area: 'usersPage' | 'teamspaces' | 'roles' | 'groups';
  children: ReactNode;
}) {
  const access = useUserManagementAccess();
  if (!access[area]) return <Navigate to="/app/screenManagement" replace />;
  return <>{children}</>;
}
